import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import GridCostCalculationFilters from "../../components/gridCost/GridCostCalculationFilters";
import GridCostResultView from "../../components/gridCost/GridCostResultView";
import { useAuth } from "../../providers/AuthContext";
import { calculateGridCost } from "../../api/GridCost";

export default function GridCalculationsPage() {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const [filters, setFilters] = useState({
        isValid: false,
        yearMonths: [],
        sitePublicIds: [],
        siteNames: [],
    });
    const [loadingSites, setLoadingSites] = useState(true);
    const [calculating, setCalculating] = useState(false);
    const [result, setResult] = useState(null);
    const [calculationError, setCalculationError] = useState(null);

    const siteFilterKey = (filters.sitePublicIds || []).join("|");

    useEffect(() => {
        setResult(null);
        setCalculationError(null);
    }, [siteFilterKey, filters.fromMonth, filters.toMonth]);

    const handleCalculate = async () => {
        if (!filters.isValid) {
            toast.error(
                filters.siteLimitError ||
                filters.monthRangeError ||
                t("gridCost.selectSiteAndMonth")
            );
            return;
        }

        setCalculating(true);
        setResult(null);
        setCalculationError(null);
        try {
            const token = await getAccessToken();
            const data = await calculateGridCost(
                {
                    sitePublicIds: filters.sitePublicIds,
                    yearMonths: filters.yearMonths,
                },
                token
            );
            setResult(data);

            const sites = data?.sites || [];
            const failedCount = sites.filter((site) => site.error).length;
            const successCount = sites.filter((site) => !site.error).length;

            if (failedCount > 0 && successCount > 0) {
                toast.error(t("gridCost.partialSiteFail", { failed: failedCount, total: sites.length }));
            } else if (failedCount > 0) {
                toast.error(t("gridCost.allSitesFailed"));
            } else {
                toast.success(t("gridCost.calculationComplete"));
            }
        } catch (err) {
            setCalculationError(err.message);
            toast.error(err.message);
        } finally {
            setCalculating(false);
        }
    };

    return (
        <>
            <HeaderBar />
            <div className="flex w-full min-h-0">
                <Sidebar />
                <main className="page">
                    <div className="page-content">
                        <h2 className="mb-2">{t("pages.gridCalculations")}</h2>
                        <p className="text-sm text-mute-600 mb-4">
                            {t("gridCost.intro")}
                        </p>

                        <GridCostCalculationFilters
                            onFiltersChange={setFilters}
                            onLoadingChange={setLoadingSites}
                            onCalculate={handleCalculate}
                            calculating={calculating}
                            loadingSites={loadingSites}
                        />

                        {loadingSites ? (
                            <LoadingSpinner text={t("gridCost.loadingSites")} className="mt-4" />
                        ) : calculating ? (
                            <LoadingSpinner text={t("gridCost.calculatingCosts")} className="mt-4" />
                        ) : calculationError ? (
                            <div
                                className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                                role="alert"
                            >
                                {calculationError}
                            </div>
                        ) : (
                            <GridCostResultView result={result} />
                        )}
                    </div>
                </main>
            </div>
        </>
    );
}
